import { ArrowRight, Check } from "lucide-react";
import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Button } from "../../../client/components/ui/button";
import useTranslation from "../../../client/hooks/useTranslation";
import { useLiteData } from "../../data/LiteDataProvider";
import { useLiteDiscipline } from "../../data/useLiteDiscipline";
import { byTag } from "../../lib/analytics";
import { activeGoals, closestGoals, goalProgress } from "../../lib/discipline";
import { inPeriod } from "../../lib/filters";
import { summarize } from "../../lib/stats";
import type { LiteTrade } from "../../lib/types";
import { AccountMini } from "../../ui/AccountCard";
import { Bar, Card, Empty, Pnl } from "../../ui/ui";

const linkCls = "text-xs text-primary hover:underline whitespace-nowrap";

/** Journée en cours : P&L, horaires, puis la discipline du jour (routine + objectifs les plus proches). */
export function TodayCard() {
  const t = useTranslation().lite.dashboard.today;
  const td = useTranslation().lite.discipline.dashboard;
  const { fmt, scopedTrades, setFilters } = useLiteData();
  const { today, goals, routines } = useLiteDiscipline();
  const todayTrades = useMemo(() => scopedTrades.filter(tr => inPeriod(tr.openedAt, "today")), [scopedTrades]);
  const dayStats = summarize(todayTrades);
  const closest = useMemo(() => closestGoals(goals, 2), [goals]);

  const range = todayTrades.length
    ? t.range(
        fmt.time(todayTrades.reduce((a, b) => (a.openedAt < b.openedAt ? a : b)).openedAt),
        fmt.time(todayTrades.reduce((a, b) => (a.closedAt > b.closedAt ? a : b)).closedAt),
      )
    : t.noTrade;
  const hasDiscipline = routines.some(r => r.isActive) || activeGoals(goals).length > 0;
  const routineComplete = today.total > 0 && today.done === today.total;

  return (
    <Card
      title={t.title}
      subtitle={fmt.dateLong(new Date())}
      action={<Link to="/trading-journal" onClick={() => setFilters({ period: "today" })} className={linkCls}>{t.seeDay}</Link>}
    >
      <div className="flex items-end justify-between gap-3">
        <Pnl value={dayStats.netPnl} className="text-3xl font-semibold" />
        <span className="text-xs text-muted-foreground text-right">{range}</span>
      </div>
      {!hasDiscipline ? (
        <div className="mt-5 rounded-xl border border-dashed border-border p-4 text-sm">
          <div className="font-medium">{td.emptyTitle}</div>
          <p className="text-muted-foreground text-xs mt-1">{td.emptyText}</p>
          <Button asChild variant="outline" size="sm" className="mt-3">
            <Link to="/discipline-manager">{td.emptyCta}</Link>
          </Button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <div className="flex items-center justify-between text-xs">
            <span className="font-medium">{td.title}</span>
            <Link to="/discipline-manager" className={`${linkCls} inline-flex items-center gap-1`}>{td.link} <ArrowRight size={12} /></Link>
          </div>
          {today.total > 0 && (
            <div>
              <div className="flex items-center justify-between gap-2 text-xs mb-1.5">
                <span className="inline-flex items-center gap-1.5 text-muted-foreground">{routineComplete && <Check size={13} className="text-pnlPositive" />}{td.routine}</span>
                <span className="font-mono tabular-nums">{today.done} / {today.total}</span>
              </div>
              <Bar value={today.done / today.total} tone={routineComplete ? "gain" : "brand"} />
            </div>
          )}
          {closest.map(g => {
            const p = goalProgress(g);
            return (
              <div key={g.id}>
                <div className="flex items-center justify-between gap-2 text-xs mb-1.5">
                  <span className="truncate text-muted-foreground">{g.title}</span>
                  <span className="font-mono tabular-nums shrink-0">{fmt.pct(p.ratio)}</span>
                </div>
                <Bar value={p.ratio} tone={p.achieved ? "gain" : "brand"} />
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
}

/** Comptes prop actifs, le plus proche de la perte max en premier (état issu du serveur). */
export function PropAccountsCard() {
  const t = useTranslation().lite.dashboard.prop;
  const { accounts, inScope } = useLiteData();
  const prop = accounts
    .filter(a => a.active && a.firm && inScope(a))
    .sort((a, b) => (a.cushion?.ratio ?? 1) - (b.cushion?.ratio ?? 1));

  return (
    <Card title={t.title} subtitle={t.subtitle} action={<Link to="/prop-firm-manager" className={linkCls}>{t.link}</Link>}>
      {prop.length ? (
        <div className="divide-y divide-border -my-1 max-h-[300px] overflow-y-auto pr-1">
          {prop.map(a => <AccountMini key={a.id} account={a} />)}
        </div>
      ) : <Empty title={t.empty} />}
    </Card>
  );
}

/** Résultat par tag, le tag négatif le plus coûteux mis en avant. */
export function BehaviorCard({ trades, className = "" }: { trades: LiteTrade[]; className?: string }) {
  const t = useTranslation().lite.dashboard.behavior;
  const { fmt } = useLiteData();
  const tags = useMemo(() => byTag(trades), [trades]);
  const worst = tags.at(-1);
  const maxAbs = Math.max(1, ...tags.map(x => Math.abs(x.s.netPnl)));

  return (
    <Card title={t.title} subtitle={t.subtitle} className={className}>
      {tags.length ? (
        <>
          {worst && worst.s.netPnl < 0 && (
            <div className="rounded-xl bg-pnlNegative/10 text-pnlNegative text-sm px-3 py-2.5 mb-4">
              {t.costs(worst.name, fmt.money(worst.s.netPnl, { sign: false }), worst.s.count)}
            </div>
          )}
          <div className="space-y-2.5 max-h-[260px] overflow-y-auto pr-1">
            {tags.map(x => (
              <div key={x.key} className="grid grid-cols-[minmax(0,110px)_1fr_80px] items-center gap-3 text-sm">
                <span className="truncate text-muted-foreground">{x.name}</span>
                <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                  <div className={`h-full rounded-full ${x.s.netPnl >= 0 ? "bg-pnlPositive" : "bg-pnlNegative"}`} style={{ width: `${(Math.abs(x.s.netPnl) / maxAbs) * 100}%` }} />
                </div>
                <Pnl value={x.s.netPnl} className="text-right text-xs" />
              </div>
            ))}
          </div>
        </>
      ) : <Empty title={t.empty} />}
    </Card>
  );
}
